import { useQuery } from "@tanstack/react-query";
import { useSelector } from "react-redux";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useList } from "$hooks/useList";
import useOrder from "$hooks/useOrder";
import { CompanyType, Order } from "$types";
import { Services } from "$utils/typeConstants";
import { CompanyServices, EntrepotService } from "$services";
import useAuth from "./useAuth";
import { appState } from "$reducers";
import { CacheInvalidationAction } from "$utils/cache";
import { normalizeArray } from "$utils/utils";
import { queryClient } from "$utils/queryClient";

export const QUANTITY_BADGE_WIDTH = "150px";

// Helper function to parse filter key and extract attribute and operator
const parseFilterKey = (
  key: string
): { attribute: string; operator: string } | null => {
  // Handle bracket notation: "suborders][dockyard" or "suborders][type][$ne"
  if (key.startsWith("suborders][")) {
    const afterSuborders = key.substring(11); // Remove "suborders]["

    // Check for operator in key: "type][$ne" -> attribute="type", operator="$ne"
    const operatorMatch = afterSuborders.match(/^([^]]+)\]\[\$([^]]+)$/);
    if (operatorMatch) {
      return {
        attribute: operatorMatch[1],
        operator: `$${operatorMatch[2]}`,
      };
    }

    // Simple case: "dockyard]" -> attribute="dockyard", operator="$in"
    const simpleMatch = afterSuborders.match(/^([^]]+)\]?$/);
    if (simpleMatch) {
      return {
        attribute: simpleMatch[1].replace(/\]$/, ""),
        operator: "$in",
      };
    }
  }

  // Handle dot notation: "suborders.dockyard"
  if (key.startsWith("suborders.")) {
    const attribute = key.substring(10); // Remove "suborders."
    // Only handle simple dot notation (no nested dots)
    if (!attribute.includes(".")) {
      return {
        attribute,
        operator: "$in",
      };
    }
  }

  return null;
};

// Helper function to normalize filter value and get operator/values
const normalizeFilterValue = (
  filterValue: any,
  keyOperator: string
): { operator: string; values: any[] } => {
  // If filter value is empty, return empty
  if (
    !filterValue ||
    (Array.isArray(filterValue) && filterValue.length === 0)
  ) {
    return { operator: keyOperator, values: [] };
  }

  // If it's an array, use it directly with the key's operator
  if (Array.isArray(filterValue)) {
    return { operator: keyOperator, values: filterValue };
  }

  // If it's an object with operators, extract the operator and values
  if (typeof filterValue === "object" && filterValue !== null) {
    if ("$eq" in filterValue) {
      return { operator: "$eq", values: [filterValue.$eq] };
    }
    if ("$in" in filterValue && Array.isArray(filterValue.$in)) {
      return { operator: "$in", values: filterValue.$in };
    }
    if ("$ne" in filterValue && Array.isArray(filterValue.$ne)) {
      return { operator: "$ne", values: filterValue.$ne };
    }
  }

  // Single primitive value
  return { operator: keyOperator, values: [filterValue] };
};

// Helper function to apply a filter to a suborder
const applyFilter = (
  suborder: any,
  attribute: string,
  operator: string,
  values: any[]
): boolean => {
  if (values.length === 0) return true; // Empty filter means no restriction

  const suborderValue = suborder[attribute];

  switch (operator) {
    case "$eq":
      return suborderValue === values[0];
    case "$in":
      return values.includes(suborderValue);
    case "$ne":
      // For $ne with array, suborder value should NOT be in the array
      return !values.includes(suborderValue);
    default:
      // Unknown operator, default to $in behavior
      return values.includes(suborderValue);
  }
};

const useEDBAvailableOrders = (
  companyId?: string,
  dockyardId?: string,
  excludedEDBId?: number,
  defaultItemsPerPage?: number
) => {
  const { defaultOptions } = useOrder();
  const { filter: companySidebarFilter } = useSelector(appState);
  const { isContributor, user } = useAuth();

  // CLIENT OWN WAREHOUSES AND LOGICTICIEN LINKED
  const [clientWarehouses, setClientWarehouses] = useState([]);
  const [isLoadingContributorCompanies, setIsLoadingContributorCompanies] =
    useState(false);

  // Query Contributor Companies
  const fetchContributorCompanies = useCallback(async () => {
    const contributorCompanyIds = [
      ...(user?.userInfo?.userCompany?.contributorOf || []),
      companySidebarFilter?._id,
    ].filter(Boolean); // Remove any undefined/null values

    if (contributorCompanyIds.length === 0) {
      setClientWarehouses([]);
      setIsLoadingContributorCompanies(false);
      return;
    }

    const filtersCC = {
      $inId: contributorCompanyIds,
    };
    const queryKeysCC = [
      CacheInvalidationAction.GET_COMPANIES,
      "contributorCompanies",
      filtersCC,
    ];
    setIsLoadingContributorCompanies(true);
    let companies: any = await queryClient.getQueryData(queryKeysCC);
    if (!companies) {
      companies = await queryClient.fetchQuery({
        queryKey: queryKeysCC,
        queryFn: () => CompanyServices.getCompanies(filtersCC),
        staleTime: 60 * 1000,
      });
      companies = companies.data;
      await queryClient.setQueryData(queryKeysCC, companies);
    }

    const filters = {
      page: 1,
      rowsPerPage: 50000,
      ignoreContributorOf: true,
    };
    let companiesTest;
    companiesTest = await queryClient.getQueryData([
      CacheInvalidationAction.GET_COMPANIES,
      filters,
    ]);
    if (!companiesTest) {
      companiesTest = await queryClient.fetchQuery({
        queryKey: [CacheInvalidationAction.GET_COMPANIES, filters],
        queryFn: () => CompanyServices.getCompanies(filters),
        staleTime: 60 * 1000,
      });
      companiesTest = companiesTest.data;
      await queryClient.setQueryData(
        [CacheInvalidationAction.GET_COMPANIES, filters],
        companiesTest
      );
    }
    // console.log("companiesTest", companiesTest);
    // Fetch warehouses for each contributor company
    const warehousesCompanyClientArray = [];
    const clientOwnWarehouses = [];
    const clientLogisticianWarehouses = [];
    await Promise.all(
      companies.map(async (companyOfContributor) => {
        const queryKeysCCWarehouses = [
          CacheInvalidationAction.GET_ENTREPOTS,
          "warehousesCompanyClient",
          { company: companyOfContributor._id },
        ];
        let warehousesCompanyClient: any = await queryClient.getQueryData(
          queryKeysCCWarehouses
        );
        if (!warehousesCompanyClient) {
          warehousesCompanyClient = await queryClient.fetchQuery({
            queryKey: queryKeysCCWarehouses,

            queryFn: () =>
              EntrepotService.getEntrepots({
                company: companyOfContributor._id,
              }),

            staleTime: 60 * 1000,
          });
          await queryClient.setQueryData(
            queryKeysCCWarehouses,
            warehousesCompanyClient
          );
        }

        warehousesCompanyClientArray.push(...warehousesCompanyClient.data);
        clientOwnWarehouses.push(
          ...(warehousesCompanyClient.data || []).filter(
            (e) => e.parentCompany === companyOfContributor._id
          )
        );

        clientLogisticianWarehouses.push(
          ...(warehousesCompanyClient.data || []).filter(
            (e) =>
              e.companies.some(
                (company) => companyOfContributor._id === company._id
              ) &&
              companiesTest
                ?.find((c) => c._id === e.parentCompany)
                ?.companyTypes?.includes(CompanyType.LOGISTICIAN)
          )
        );
      })
    );
    // console.log("warehousesCompanyClientArray", warehousesCompanyClientArray);

    // Get unique warehouses by _id
    const uniqueClientOwnWarehouses = clientOwnWarehouses.filter(
      (warehouse, index, self) =>
        index === self.findIndex((w) => w._id === warehouse._id)
    );
    const uniqueClientLogisticianWarehouses =
      clientLogisticianWarehouses.filter(
        (warehouse, index, self) =>
          index === self.findIndex((w) => w._id === warehouse._id)
      );

    setClientWarehouses([
      ...uniqueClientOwnWarehouses.filter((w) => w.company._id === companyId),
      ...uniqueClientLogisticianWarehouses,
    ]);
    setIsLoadingContributorCompanies(false);
  }, [
    user?.userInfo?.userCompany?.contributorOf,
    companyId,
    companySidebarFilter?._id,
  ]);

  useEffect(() => {
    if (isContributor()) {
      fetchContributorCompanies();
    }
  }, [fetchContributorCompanies, isContributor]);

  const userCompanyId = isContributor()
    ? user.userInfo.userCompany._id
    : companySidebarFilter?._id;

  const { data: warehouses } = useQuery({
    queryKey: [
      CacheInvalidationAction.GET_ENTREPOTS,
      { company: userCompanyId },
    ],

    queryFn: () =>
      EntrepotService.getEntrepots({
        company: userCompanyId, // in ordersList on utilise parentCompany et non pas company ... hum hum
      }),

    enabled: !!userCompanyId,
    gcTime: 5 * 1000 * 60,
  }) as any;

  const warehouseIds = useMemo(() => {
    if (!warehouses?.data) return [];
    return [
      ...new Set([
        ...warehouses.data.map((w: any) => w._id),
        ...clientWarehouses.map((w: any) => w._id),
      ]),
    ];
  }, [warehouses, clientWarehouses]);

  // Build default filters with proper syntax for the new implementation
  const defaultFilters = useMemo(() => {
    const filters: Record<string, any> = {};

    if (companyId) {
      filters.company = { $eq: companyId };
    }

    // Fix: Use bracket notation that matches what the UI components expect
    if (dockyardId) {
      filters["suborders][dockyard"] = [dockyardId];
    }

    // Exclude stock: only orders with at least one suborder dockyard or project (Strapi format; gateway converts to suborderType)
    filters["suborders][type][$ne"] = ["stock"];

    // Add empty arrays for references
    filters["receptions.receptionReferences.orders.reference"] = [];
    filters.reference = [];
    filters.receptionId = [];

    // // Add contributor-specific filters
    if (isContributor()) {
      filters.warehouse = warehouseIds; // This will be transformed to $in internally
      filters.status = "delivered_ok";
    }

    return filters;
  }, [companyId, dockyardId, isContributor, warehouseIds]);

  // Convert default sort if needed
  const defaultSort = useMemo(() => {
    if (!defaultOptions.defaultSort) return undefined;

    if (typeof defaultOptions.defaultSort === "string") {
      const [field, direction = "asc"] = defaultOptions.defaultSort.split(":");
      return {
        field,
        direction: direction as "asc" | "desc",
      };
    }

    return defaultOptions.defaultSort;
  }, [defaultOptions.defaultSort]);

  // Use the new useList hook
  const {
    data: availableOrders,
    isLoading: isFetching,
    filters,
    setFilter: originalSetFilter,
    setFilters: originalSetFilters,
    pagination,
  } = useList<Order & { id: string | number }>(
    "api/order/orders/consolidated",
    {
      populate: defaultOptions.populate,
      defaultPageSize: defaultItemsPerPage || 20,
      defaultFilters,
      defaultSort,
      ignoreSideBarFilter: isContributor(),
      additionalParams: {
        withIntegrableQuantities: true,
        excludedEDBId,
      },
      enabled: !isLoadingContributorCompanies,
      service: Services.GATEWAY_API,
    }
  );

  const setFilters = useCallback(
    (newFilters: Record<string, any>) => {
      if (isContributor()) {
        const filtersToSet = { ...newFilters };
        if (!filtersToSet.warehouse && warehouseIds.length > 0) {
          filtersToSet.warehouse = warehouseIds;
        }
        originalSetFilters(filtersToSet);
      } else {
        originalSetFilters(newFilters);
      }
    },
    [originalSetFilters, isContributor, warehouseIds]
  );

  const setFilter = useCallback(
    (key: string, value: any) => {
      if (key === "warehouse" && isContributor()) {
        // If warehouse filter is being cleared/emptied, set it to all warehouses
        if (!value || (Array.isArray(value) && value.length === 0)) {
          originalSetFilter(key, warehouseIds);
        } else {
          originalSetFilter(key, value);
        }
      } else {
        originalSetFilter(key, value);
      }
    },
    [originalSetFilter, isContributor, warehouseIds]
  );

  const resetFilters = () => {
    setFilters(defaultFilters);
  };

  const displayFilters = useMemo(() => {
    if (!isContributor()) {
      return filters;
    }

    // For contributors, only show warehouse filter if it's not the default (all warehouses)
    const currentWarehouse = filters.warehouse;
    const isDefaultWarehouse =
      Array.isArray(currentWarehouse) &&
      Array.isArray(warehouseIds) &&
      currentWarehouse.length === warehouseIds.length &&
      currentWarehouse.every((id) => warehouseIds.includes(id));

    if (isDefaultWarehouse) {
      const { warehouse: _warehouse, ...filtersWithoutWarehouse } = filters;
      return filtersWithoutWarehouse;
    }

    return filters;
  }, [filters, isContributor, warehouseIds]);

  // Manual filtering of suborders (not handled by Strapi automatically)
  const filteredOrders = useMemo(() => {
    const ordersArray = normalizeArray(availableOrders);

    if (ordersArray.length === 0) {
      return [];
    }

    // Extract and normalize all suborder filters
    const suborderFilters: Array<{
      attribute: string;
      operator: string;
      values: any[];
      source: string;
    }> = [];
    const processedAttributes = new Set<string>();

    // Process filters, giving priority to bracket notation over dot notation
    const filterKeys = Object.keys(filters || {}).sort((a, b) => {
      // Bracket notation comes first
      if (a.startsWith("suborders][") && !b.startsWith("suborders]["))
        return -1;
      if (!a.startsWith("suborders][") && b.startsWith("suborders][")) return 1;
      return 0;
    });

    filterKeys.forEach((key) => {
      const parsed = parseFilterKey(key);
      if (!parsed) return;

      const { attribute, operator: keyOperator } = parsed;

      // Skip if we already processed this attribute (bracket notation takes priority)
      if (processedAttributes.has(attribute)) return;

      const filterValue = filters[key];
      const { operator, values } = normalizeFilterValue(
        filterValue,
        keyOperator
      );

      if (values.length > 0) {
        suborderFilters.push({
          attribute,
          operator,
          values,
          source: key,
        });
        processedAttributes.add(attribute);
      }
    });

    return ordersArray
      ?.map((o) => ({
        ...o,
        hasOnlyOneSuborder: Array.isArray(o.suborders)
          ? o.suborders.length === 1
          : false,
      }))
      .reduce((res: (Order & { hasOnlyOneSuborder: boolean })[], order) => {
        const subordersArray = Array.isArray(order.suborders)
          ? order.suborders
          : [];

        const filteredSuborders = subordersArray.filter((suborder) => {
          // Apply all filters - suborder must pass ALL filters to be included
          return suborderFilters.every((filter) => {
            return applyFilter(
              suborder,
              filter.attribute,
              filter.operator,
              filter.values
            );
          });
        });

        return filteredSuborders.length === 0
          ? res
          : [
              ...res,
              {
                ...order,
                suborders: filteredSuborders,
              },
            ];
      }, []);
  }, [availableOrders, filters]);

  return {
    availableOrders: filteredOrders,
    isFetching,
    filters: displayFilters,
    resetFilters,
    setFilter,
    setPageIndex: pagination.setPageIndex,
    pageIndex: pagination.pageIndex,
    pageCount: pagination.pageCount,
    setItemsPerPage: pagination.setPageSize,
    itemsPerPage: pagination.pageSize,
    itemsCount: pagination.total,
    QUANTITY_BADGE_WIDTH,
  };
};

export default useEDBAvailableOrders;
